import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";

import { LinkButton } from "~frontend/components/Link";

import ActiveCampaignSwitcherList from "./ActiveCampaignSwitcherList";
import { CampaignNameAndId, Thread } from "./types";

export default function ActiveCampaignSwitcher({
  contextCampaign,
  onChange,
  thread,
  hasUnsavedChanges,
  onDiscardChanges,
}: {
  contextCampaign: CampaignNameAndId;
  onChange: (newCampaignId: string) => void;
  thread: Thread;
  hasUnsavedChanges: () => boolean;
  onDiscardChanges: () => void;
}): ReactElement {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );
  const confirm = useConfirm();

  async function handleLinkClick(evt: React.MouseEvent<HTMLAnchorElement>) {
    evt.preventDefault();

    if (hasUnsavedChanges()) {
      const { confirmed } = await confirm({
        title: (
          <FormattedMessage
            defaultMessage="Discard unsaved changes?"
            description="Title for warning dialog when discarding saved changes"
          />
        ),
        description: (
          <FormattedMessage
            defaultMessage="Changing the active campaign will discard your message, contact notes, and tags. Are you sure you want to discard your changes?"
            description="Body for warning dialog when discarding saved changes"
          />
        ),
      });
      if (!confirmed) {
        return;
      }

      onDiscardChanges();
    }

    setDialogOpen(true);
    setSelectedCampaignId(null);
  }

  if (!contextCampaign || !contextCampaign.name) {
    return <div />;
  }

  return (
    <Box sx={{ textAlign: "right", fontSize: "10px", marginBottom: "16px" }}>
      <FormattedMessage
        defaultMessage="You are viewing details for the “{campaignName}” campaign."
        description="Info message showing the active campaign in the texter view"
        values={{
          campaignName: contextCampaign.name,
        }}
      />

      <LinkButton onClick={handleLinkClick}>
        <FormattedMessage
          defaultMessage="Change Campaign"
          description="Link text to change the active campaign in the texter view"
        />
      </LinkButton>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          <FormattedMessage
            defaultMessage="Change Campaign"
            description="Dialog title to change the active campaign in the texter view"
          />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage
              defaultMessage="Change the campaign to view associated scripts, tags, and add relevant conversation notes."
              description="Dialog text when changing the active campaign in the texter view"
            />
          </DialogContentText>
          {dialogOpen && (
            <ActiveCampaignSwitcherList
              selectedCampaignId={selectedCampaignId}
              setSelectedCampaignId={setSelectedCampaignId}
              thread={thread}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            <FormattedMessage
              defaultMessage="Cancel"
              description="Cancel button text when changing the active campaign in the texter view"
            />
          </Button>
          <Button
            onClick={() => {
              setDialogOpen(false);
              onChange(selectedCampaignId!);
            }}
            variant="contained"
            color="secondary"
            disabled={!selectedCampaignId}
          >
            <FormattedMessage
              defaultMessage="Change campaign"
              description="Button text when changing the active campaign in the texter view"
            />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
